import { useTranslation } from 'react-i18next'
import { useState, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { TbSend } from "react-icons/tb";
import { PiBuildingApartmentFill } from "react-icons/pi";
import { getConversationFromChatId, sendMessageToFacility, sendChatFile, sendAudioFile, updateMessageReadStatus } from '../../../services/chatService'
import { streamChatbotMessage } from '../../../services/chatbotService'
import { createOrGetAdminConversation, getAdminChatMessages, sendAdminChatMessage, uploadAdminChatFile } from '../../../services/adminChatService'
import { joinChatRoom, leaveChatRoom, onNewMessage, offNewMessage, joinAdminChatRoom, leaveAdminChatRoom, onAdminChatMessage, offAdminChatMessage } from '../../../services/socketService'
import { IoMdArrowRoundBack } from "react-icons/io";
import { RiRobot2Line } from "react-icons/ri";
import { MdAttachFile, MdSupportAgent } from "react-icons/md";
import { BsMic } from "react-icons/bs";

//upload photo
import { RxCross2 } from "react-icons/rx";
import { HiOutlinePhotograph } from "react-icons/hi";
import { IoIosDocument } from "react-icons/io";

import FacilityConversations from './conversations/FacilityConversations'
import AudioRecorder from './AudioRecorder'
import AudioPlayer from './AudioPlayer'

const ConversationChat = (props) => {
    const { t } = useTranslation()
    const user = useSelector(state => state.login.data)
    const token = user ? user.token : null
    const MAX_SIZE = 25 * 1024 * 1024;

    const [selectedConversation, setSelectedConversation] = useState(null)
    const [messages, setMessages] = useState([])
    const [currentMessage, setCurrentMessage] = useState('')
    const [loading, setLoading] = useState(false)
    const [loadingMessages, setLoadingMessages] = useState(false)
    const [errors, setErrors] = useState({})
    const [files, setFiles] = useState([])
    const [images, setImages] = useState([])
    const [typeError, setTypeError] = useState(null)
    const [selectedSection, setSelectedSection] = useState('Contacts')
    const [adminConversationId, setAdminConversationId] = useState(null)

    // Chatbot-specific state (separate from facility chat "messages" above,
    // since the chatbot uses a role-based {role, content} shape and streams
    // token-by-token rather than fetching a fixed message history).
    const [chatbotMessages, setChatbotMessages] = useState([
        { id: 'bot-sys-1', role: 'assistant', content: t('chat.bot_ask') }
    ])
    const [chatbotSessionId, setChatbotSessionId] = useState(null)
    const [chatbotLoading, setChatbotLoading] = useState(false)
    const chatbotAbortRef = useRef(null)

    const containerRef = useRef(null);
    

    useEffect(() => {
        if (containerRef && containerRef.current) containerRef.current.scrollTop = containerRef.current.scrollHeight;
        
    }, [messages, chatbotMessages]);

    const [recording, setRecording] = useState(false)

    const handleChange = (e) => {
        setCurrentMessage(e.target.value)
    }

    const getConversation = async () => {
        try {
            setLoadingMessages(true)
            if (selectedConversation.id === 'chatbot') {
                setMessages([])
                return
            }
            if (selectedConversation.id === 'support') {
                let convId = adminConversationId
                if (!convId) {
                    const convResult = await createOrGetAdminConversation(token)
                    if (convResult.success) {
                        convId = convResult.data.id || convResult.data
                        setAdminConversationId(convId)
                    } else {
                        setErrors({...errors, messagesError: convResult.error})
                        return
                    }
                }
                const supportResult = await getAdminChatMessages(token, convId)
                if (supportResult.success) {
                    setMessages(supportResult.data)
                } else {
                    setErrors({...errors, messagesError: supportResult.error})
                }
                return
            }
            const result = await getConversationFromChatId(token,selectedConversation.id)
            if (result.success) {
                setMessages(result.data)
            } else {
                setErrors({...errors, messagesError: result.error})
            }
        } catch (err) {

        } finally {
            setLoadingMessages(false)
        }
    }

    const sendChatbotMessage = async () => {
        const userMessage = currentMessage.trim()
        if (!userMessage || chatbotLoading) return

        const userId = `user-${Date.now()}`
        const assistantId = `assistant-${Date.now()}`

        setCurrentMessage('')
        setChatbotMessages(prev => [
            ...prev,
            { id: userId, role: 'user', content: userMessage },
            { id: assistantId, role: 'assistant', content: '' },
        ])
        setChatbotLoading(true)

        chatbotAbortRef.current?.abort?.()
        const controller = new AbortController()
        chatbotAbortRef.current = controller

        await streamChatbotMessage({
            message: userMessage,
            sessionId: chatbotSessionId,
            signal: controller.signal,
            onSessionId: (id) => setChatbotSessionId(id),
            onToken: (token) => {
                setChatbotMessages(prev => prev.map(m =>
                    m.id === assistantId ? { ...m, content: (m.content || '') + token } : m
                ))
            },
            onDone: () => setChatbotLoading(false),
            onError: () => {
                setChatbotMessages(prev => prev.map(m =>
                    m.id === assistantId ? { ...m, content: t('chat.bot_unavailable') } : m
                ))
                setChatbotLoading(false)
            },
        })
    }

    const sendMessage = async () => {
        if (selectedConversation?.id === 'chatbot') {
            return sendChatbotMessage()
        }
        console.log('sending chat message')
        if (currentMessage == "") return
        try {
            if (selectedConversation?.id === 'support') {
                const supportObj = {
                    conversationId: adminConversationId,
                    message: currentMessage,
                    messageType: "TEXT"
                }
                const supportResult = await sendAdminChatMessage(token,supportObj)
                if (supportResult.success) {
                    setMessages([...messages, {message: currentMessage, senderType: "USER", messageType: "TEXT"}])
                    setCurrentMessage('')
                } else {
                    setErrors({...errors, sendMessageError: t('chat.send_message_failed')})
                }
                return
            }
            const obj = {
                chatRequestId: selectedConversation.id,
                message: currentMessage,
                messageType: "TEXT"
            }
            // console.log('message being sent: ',obj)
            const result = await sendMessageToFacility(token,obj)
            if (result.success) {
                //we need to append this message to the previous ones and display it
                setMessages([...messages, {message: currentMessage, senderType: "USER", messageType: "TEXT"}])
                setCurrentMessage('')
            } else {
                //indicate to the user that it wasn't sent
            }
        } catch (err) {
                console.error("Failed to send message: ",err)
                setErrors({...errors, sendMessageError: t('chat.send_message_failed')})
        }
    }

    const sendFile = async (messageType) => {
        console.log('sending chat file')
        try {
            if (selectedConversation?.id === 'support') {
                const items = messageType === 'IMAGE' ? images.map((item) => item.file) : files
                const sendErrors = []
                for (let i = 0; i < items.length; i++) {
                    const fd = new FormData()
                    fd.append('file', items[i])
                    fd.append('conversationId', adminConversationId)
                    fd.append('message', currentMessage || '')
                    const uploadResult = await uploadAdminChatFile(token, fd)
                    if (!uploadResult.success) sendErrors.push(uploadResult.error)
                }
                if (sendErrors.length === 0) {
                    setCurrentMessage('')
                    getConversation()
                }
                setFiles([])
                setImages([])
                return
            }
            const obj = { //include list of file size and name
                chatRequestId: selectedConversation.id,
                message: currentMessage,
                messageType: messageType,
                fileSizes: messageType == 'IMAGE' ? images.map((item) => item.file.size) : files.map((item) => item.size),
                fileNames: messageType === 'IMAGE' ? images.map((item) => item.file.name) :  files.map((item) => item.name),
                formDatas: messageType === 'IMAGE' ? images.map((item) => {
                    const fd = new FormData()
                    fd.append('file', item.file)
                    return fd
                }) : files.map((item) => {
                    const fd = new FormData()
                    fd.append('file', item)
                    return fd
                })
            }
            const result = await sendChatFile(token,obj)
            if (result.errors.length == 0) { // Some immediate update to show the new image or file
                setCurrentMessage('')
                getConversation()
            } else {
                // indicate that the file was not sent
                // console.log('Encountered errors sending files: ', result.errors)
            }
            //reset
        } catch (err) {
            console.error("Failed to send file: ",err)
            setErrors({...errors, sendFileError: t('chat.send_file_failed')})
        } finally {
            setFiles([])
            setImages([])
        }
    }

    const handleImageChange = (e) => {
        // console.log('upload chat image file: ', e.target.files)
        if (e.target.files[0].size > MAX_SIZE) {
            setErrors({...errors, imageError: t('chat.image_too_large')})
            return
        }
        if (e.target.files && (e.target.files[0].type == 'image/png' || e.target.files[0].type == 'image/jpeg' || e.target.files[0].type == 'image/webp' || e.target.files[0].type == 'image/gif')) {
            // console.log(e.target.files)
            // setFile([...file,e.target.files[0]])
            const reader = new FileReader();
            reader.addEventListener("load", () => {
                setImages([...images, {file:e.target.files[0] ,image:reader.result}]);
            });
            reader.readAsDataURL(e.target.files[0]);
            setTypeError(false)
        } else {
            setErrors({...errors, imageError: t('chat.image_type')})
            setTypeError(true)
        }
    }

    const handleFileChange = (e) => {
        // console.log('upload chat file: ', e.target.files)
        if (e.target.files[0].size > MAX_SIZE) {
            setErrors({...errors, fileError: t('chat.file_too_large')})
            return
        }
        if (e.target.files) {
            // console.log(e.target.files)        
            setFiles([...files, e.target.files[0]]);
            setTypeError(false)
        } else {
            setErrors({...errors, fileError: t('chat.file_select_failed')})
            setTypeError(true)
        }
    }

    const uploadAudio = async ({duration, blob}) => {
        setRecording(false)
        // console.log('audio duration: ',duration)
        // console.log('audio blob: ',blob)
        try {
            const formData = new FormData()
            formData.append('file', blob, `recording-${Date.now()}.webm`)
            formData.append("resource_type", "video"); // Cloudinary treats audio as "video"
            formData.append("format", "mp3"); // tells Cloudinary to transcode to mp3

            if (selectedConversation?.id === 'support') {
                formData.append('conversationId', adminConversationId)
                formData.append('message', currentMessage || '')
                const audioResult = await uploadAdminChatFile(token, formData)
                if (audioResult.success) {
                    setCurrentMessage('')
                    getConversation()
                } else {
                    setErrors({...errors, audioError: t('chat.audio_send_failed')})
                }
                return
            }

            const obj = { //include list of file size and name
                    chatRequestId: selectedConversation.id,
                    message: currentMessage,
                    messageType: 'AUDIO',
                    fileSize: blob.size,
                    fileName: `audio-${duration.toFixed(2)}`,
                    formData: formData
            }
            const result = await sendAudioFile(token,obj)
            if (result.errors.length == 0) { // Some immediate update to show the new image or file
                setCurrentMessage('')
                getConversation()
            } else {
                // indicate that the file was not sent
                setErrors({...errors, audioError: t('chat.audio_send_failed')})
                // console.log('Encountered errors sending files: ', result.errors)
            }
        } catch (err) {
            console.error("Failed to send audio: ",err)
            setErrors({...errors, audioError: t('chat.audio_failed')})
        } 
    }


    useEffect(() => {
        if(!selectedConversation || !token) return
        getConversation()
    },[selectedConversation])

    // Live updates: join the relevant socket room for the active conversation
    // and append incoming messages without requiring a manual refresh.
    useEffect(() => {
        if (!token || !selectedConversation) return
        if (selectedConversation.id === 'chatbot') return

        if (selectedConversation.id === 'support') {
            if (!adminConversationId) return
            joinAdminChatRoom(adminConversationId)

            const handleAdminMessage = (msg) => {
                if (msg.conversationId !== adminConversationId) return
                if (msg.senderType === 'USER') return // already shown optimistically
                setMessages((prev) => [...prev, msg])
            }
            onAdminChatMessage(handleAdminMessage)

            return () => {
                offAdminChatMessage(handleAdminMessage)
                leaveAdminChatRoom(adminConversationId)
            }
        }

        const chatRequestId = selectedConversation.id
        joinChatRoom(chatRequestId)

        const handleNewMessage = (msg) => {
            if (msg.chatRequestId !== chatRequestId) return
            if (msg.senderType === 'USER') return // already shown optimistically
            setMessages((prev) => [...prev, msg])
        }
        onNewMessage(handleNewMessage)

        return () => {
            offNewMessage(handleNewMessage)
            leaveChatRoom(chatRequestId)
        }
    }, [selectedConversation, adminConversationId, token])

    return(
        <div className='w-full min-h-96 h-full md:h-[450px] lg:h-[500px] border-b border-[var(--light-border-color)] grid grid-cols-[1fr] place-items-center md:grid-cols-[210px_1fr] lg:grid-cols-[250px_1fr] xl:grid-cols-[300px_1fr] lg:rounded-br-lg lg:rounded-bl-lg'>
            {/*Conversations*/}
            
            <div className={`h-full w-full border-r border-[var(--light-border-color)] overflow-y-auto ${selectedSection === 'Contacts' ? 'flex' : 'hidden'} md:flex flex-col items-center justify-start`}>
                <div className='w-full h-12 border-b border-[var(--light-border-color)] flex items-center justify-start px-3 py-2 gap-4 cursor-pointer hover:bg-gray-50' onClick={()=>{
                    setSelectedConversation({id: 'chatbot', facility: {name: 'ChatBot'}, imageUrl: null})
                    setSelectedSection('Messages')
                }}>
                    <RiRobot2Line className='text-[var(--secondary-color)] h-8 w-8' />
                    <p className='text-gray-500 text-base md:text-lg'>{t('chat.chatbot')}</p>
                </div>
                <div className='w-full h-12 border-b border-[var(--light-border-color)] flex items-center justify-start px-3 py-2 gap-4 cursor-pointer hover:bg-gray-50' onClick={()=>{
                    setSelectedConversation({id: 'support', facility: {name: 'Customer Support'}, imageUrl: null})
                    setSelectedSection('Messages')
                }}>
                    <MdSupportAgent className='text-[var(--secondary-color)] h-8 w-8' />
                    <p className='text-gray-500 text-base md:text-lg'>{t('chat.customer_support')}</p>
                </div>
                <div className='w-full h-10 border-b border-[var(--light-border-color)] flex items-center justify-between px-3 py-2 bg-gray-50'>
                    <p className='text-base md:text-lg text-gray-500 font-medium'>{t('chat.facilities')}</p>
                </div>
                <FacilityConversations onClick={(item)=>{
                    setSelectedConversation(item)
                    setSelectedSection('Messages')
                }} />
            </div>

            
            {/*Chat Window */}
            <div className={`w-full min-h-96 h-full md:h-[450px] lg:h-[500px] ${selectedSection === 'Messages' ? 'flex' : 'hidden'} md:flex flex-col justify-end items-center border-l border-[var(--light-border-color)]`}>
                {loadingMessages ? <img src="/secondary-color-spinner.svg" className="w-12 h-12" /> 
                : selectedConversation ? (<>
                    <div className='w-full border-b border-[var(--light-border-color)] bg-white flex items-center justify-start h-16 gap-3 md:gap-6'>
                        <IoMdArrowRoundBack className='absolute top-3 left-4 text-gray-700 h-8 w-8 cursor-pointer md:hidden' onClick={()=>setSelectedSection('Contacts')} />
                        <div className='border border-[var(--light-border-color)] h-8 md:h-10 w-8 md:w-10 rounded-full ml-2 flex items-center justify-center bg-gray-200'>
                            {selectedConversation?.id === 'chatbot' ? <RiRobot2Line className='text-[var(--secondary-color)] h-5 md:h-7 w-5 md:w-7' /> : selectedConversation?.id === 'support' ? <MdSupportAgent className='text-[var(--secondary-color)] h-5 md:h-7 w-5 md:w-7' /> : selectedConversation?.imageUrl ? <img src={selectedConversation?.imageUrl} className="max-h-full max-w-full object-cover"/> : <PiBuildingApartmentFill className='text-gray-500 h-5 md:h-7 w-5 md:w-7' />}
                        </div>
                        <h3 className="text-gray-500 text-sm md:text-base m-0">{selectedConversation?.facility?.name}</h3>
                    </div>

                    <div ref={containerRef} id='message window' className='h-full overflow-y-auto flex flex-col gap-4 w-full p-2'>
                        {selectedConversation?.id === 'chatbot' ? (
                            chatbotMessages.map((m) => (
                                m.role === 'user'
                                    ? <p key={m.id} className='self-end bg-[#2aa1a3] p-2 h-auto w-fit max-w-[80%] ml-6 text-white text-right rounded-md whitespace-pre-wrap'>{m.content}</p>
                                    : <p key={m.id} className='self-start p-2 h-auto w-fit max-w-[80%] mr-6 border border-[var(--light-border-color)] rounded-md text-left text-gray-500 whitespace-pre-wrap'>{m.content || (chatbotLoading ? '…' : '')}</p>
                            ))
                        ) : messages.length > 0 ? messages?.map((item,index) => {
                            // text
                            if (!item.isRead) {
                                updateMessageReadStatus(token,item.id)
                            }
                            if (item.messageType === "TEXT") {
                                if (item.senderType === "USER") return <p key={index} className='self-end bg-[#2aa1a3] p-2 h-auto w-fit ml-6 text-white text-right rounded-md'>{item.message}</p>
                                return <p key={index} className='self-start p-2 h-auto w-fit mr-6 border border-[var(--light-border-color)] rounded-md text-left text-gray-500'>{item.message}</p>
                            } else if (item.messageType === 'IMAGE') {
                                return <img key={index} src={item.fileUrl} alt={item.fileName} className={`${item.senderType === 'USER' ? 'self-end' : 'self-start'} rounded-md w-32 h-32 md:w-40 md:h-40 xl:w-52 xl:h-52`} />
                            } else if (item.messageType === "FILE") {
                                return (<a href={item.fileUrl}
                                            download={item.fileName}
                                            target="_blank"
                                            rel="noopener noreferrer" key={index} className={`${item.senderType === 'USER' ? 'self-end' : 'self-start'} bg-gray-200 rounded-lg flex flex-col items-center justify-center hover:bg-gray-100 cursor-pointer h-auto min-h-28 w-auto min-w-28 p-4`}>
                                    <IoIosDocument className='text-gray-500 h-12 w-12 lg:h-16 lg:w-16' />
                                    <p className='mt-1 text-gray-600 text-sm text-center truncate'>{item.fileName}</p>
                                    </a>)
                            } else {
                                return <AudioPlayer fileUrl={item.fileUrl} fileName={item.fileName} senderType={item.senderType} duration={parseFloat(item.fileName.split('-')[1]).toFixed(2)}/>
                            }


                            //audio                            
                        }) : <h3 className="text-sm lg:text-base text-gray-400 my-auto mx-auto">{t('chat.begin_conversation')}</h3>}
                    </div>
                    <div className='w-full h-auto flex flex-col items-center justify-center border-t border-[var(--light-border-color)]'>
                        {recording && <AudioRecorder onCancel={()=>{
                            setRecording(false)
                        }} onConfirm={uploadAudio}/>}
                        {errors.audioError && <div className='w-full px-3 py-1 text-red-500 text-sm'>{errors.audioError}</div>}
                        {errors.imageError && <div className='w-full px-3 py-1 text-red-500 text-sm'>{errors.imageError}</div>}
                        {images.length > 0 && <div className='w-full h-auto flex items-center justify-start overflow-x-auto py-2 px-3 gap-2 m-0'>
                            {/* <div className='h-16 w-16 rounded-xl flex items-center justify-center bg-gray-100'>
                                <HiOutlinePhotograph className="text-gray-600 w-14 h-14"/>
                            </div> */}
                            {images?.map((item,index) => (
                                <div key={index} className='h-16 w-16 rounded-xl flex items-center justify-center relative'>
                                    <img src={item.image} className='object-cover max-h-full max-w-full h-full w-full rounded-xl' />
                                    <div className='absolute top-0 right-0 bg-gray-300 rounded-full h-5 w-5 flex items-center justify-center cursor-pointer' onClick={()=>{setImages(images.filter(k => k.file.name != item.file.name))}}>
                                        <RxCross2 className='text-gray-600 h-3 w-3' />
                                    </div>
                                </div>
                            ))}
                            </div>}

                        {errors.fileError && <div className='w-full px-3 py-1 text-red-500 text-sm'>{errors.fileError}</div>}
                        {files.length > 0 && <div className='w-full h-auto flex items-center justify-start overflow-x-auto py-2 px-3 gap-2 m-0'>
                            {/* <div className='h-16 w-16 rounded-xl flex items-center justify-center bg-gray-100'>
                                <HiOutlinePhotograph className="text-gray-600 w-14 h-14"/>
                            </div> */}
                            {files?.map((item,index) => (
                                <div key={index} className='h-16 w-16 rounded-xl flex items-center justify-center relative bg-gray-200'>
                                    <IoIosDocument className='text-gray-500 h-8 w-8 lg:h-12 lg:w-12' />
                                    <div className='absolute top-0 right-0 bg-gray-300 rounded-full h-5 w-5 flex items-center justify-center cursor-pointer' onClick={()=>{setFiles(files.filter(k => k.name != item.name))}}>
                                        <RxCross2 className='text-gray-600 h-3 w-3' />
                                    </div>
                                </div>
                            ))}
                            </div>}
                        
                        <div className='w-full h-20 flex items-center justify-between gap-2 p-2 m-0'>
                            <input className='border border-[var(--light-border-color)] h-12 w-full rounded-lg m-0 focus:outline-none text-gray-400' type='text' placeholder={selectedConversation?.id === 'chatbot' && chatbotLoading ? t('chat.waiting_response') : t('chat.enter_message')} value={currentMessage} onChange={handleChange} disabled={selectedConversation?.id === 'chatbot' && chatbotLoading} onKeyDown={(e) => {
                                if (e.key != 'Enter' || currentMessage === '') return
                                sendMessage()
                            }}>

                            </input>

                            {selectedConversation?.id !== 'chatbot' && (
                                <>
                                    <button disabled={(files.length > 0 || images.length > 0)} className='m-0 p-0 bg-transparent' onClick={()=>setRecording(true)}>
                                        <BsMic className='text-gray-500 w-6 h-6 lg:h-8 lg:w-8 cursor-pointer' />
                                    </button>

                                    <label className='m-0' htmlFor='photo-upload'>
                                        <HiOutlinePhotograph className='text-gray-500 hover:text-gray-400 w-7 h-7 lg:h-9 lg:w-9 cursor-pointer' />
                                        <input type='file' hidden onChange={handleImageChange} accept="image/*" id='photo-upload' disabled={files.length > 0 ? true : false}/>
                                    </label>                       
                                    
                                    <label className='m-0' htmlFor='file-upload'>
                                        <MdAttachFile className='text-gray-500 hover:text-gray-400 w-7 h-7 lg:h-8 lg:w-8 cursor-pointer' />
                                        <input type='file' hidden onChange={handleFileChange} accept="*" id='file-upload' disabled={images.length > 0 ? true : false}/>
                                    </label>
                                </>
                            )}

                            <div className='rounded-md lg:rounded-xl bg-gradient-to-r from-[#1a7071] to-[#26c5c7] h-8 w-10  lg:h-10 lg:w-12 p-1 cursor-pointer hover:bg-opacity-80 flex items-center justify-center' onClick={()=>{
                                if (selectedConversation?.id === 'chatbot') {
                                    sendMessage()
                                    return
                                }
                                if (images.length == 0 && files.length == 0) {
                                    sendMessage()
                                } else {
                                    images.length > 0 ? sendFile('IMAGE') : sendFile('FILE')
                                }
                                }}>
                                <TbSend className='text-white h-4 w-4 lg:h-6 lg:w-6 '/>
                            </div>
                        </div>
                    </div>
                </>)
                :
                (
                    <h3 className="text-gray-400 text-base xl:text-lg self-center my-auto px-8">{t('chat.choose_conversation')}</h3>
                )}
                
            </div>

        </div>
    )
}

export default ConversationChat  
